import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiOutlinePhotograph,
  HiOutlineVideoCamera,
  HiOutlineX,
  HiOutlineDownload,
  HiOutlineShare,
  HiOutlineHeart,
  HiOutlineSparkles,
  HiOutlineCamera,
  HiOutlineCollection,
  HiOutlineOfficeBuilding
} from "react-icons/hi";
// import Header from "../Header/Header";
import Cookies from "js-cookie";

const Gallery = () => {
  const [activeTab, setActiveTab] = useState("post");
  const [galleryData, setGalleryData] = useState({ images: [], videos: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [mediaWithCollege, setMediaWithCollege] = useState([]);

  useEffect(() => {
    const fetchGalleryData = async () => {
      try {
        const token = Cookies.get("userToken");
        if (!token) {
          setError("User not authenticated");
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/apis/user/getAllGallery`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        console.log("Gallery data:", data);

        if (!response.ok) {
          setError(data?.message || "Failed to fetch gallery data");
          return;
        }

        // Store full items with metadata
        setMediaWithCollege(data.data || []);
        
        // Extract just URLs for backward compatibility
        const images = [];
        const videos = [];
        data.data.forEach((item) => {
          if (item.image) images.push({ url: item.image, collegeName: item.adminId?.collegeName || "Unknown College" });
          if (item.video) videos.push({ url: item.video, collegeName: item.adminId?.collegeName || "Unknown College" });
        });

        setGalleryData({
          images,
          videos,
        });
      } catch (error) {
        console.error("Error fetching gallery data:", error);
        setError("Something went wrong while fetching gallery data.");
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryData();
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const heroVariants = {
    hidden: { opacity: 0, scale: 1.2 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 1.2, ease: "easeOut" }
    }
  };

  const handleMediaClick = (media, index) => {
    setSelectedMedia(media);
    setSelectedMediaIndex(index);
  };

  const handleNext = () => {
    const currentList = activeTab === 'post' ? galleryData.images : galleryData.videos;
    const nextIndex = (selectedMediaIndex + 1) % currentList.length;
    setSelectedMedia(currentList[nextIndex]);
    setSelectedMediaIndex(nextIndex);
  };

  const handlePrev = () => {
    const currentList = activeTab === 'post' ? galleryData.images : galleryData.videos;
    const prevIndex = (selectedMediaIndex - 1 + currentList.length) % currentList.length;
    setSelectedMedia(currentList[prevIndex]);
    setSelectedMediaIndex(prevIndex);
  };

  const SkeletonLoader = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
      {[...Array(8)].map((_, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="h-64 bg-gradient-to-r from-gray-200 to-gray-300 rounded-2xl animate-pulse"
        />
      ))}
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="w-full min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden"
    >
      {/* Animated Background Elements - Light Gray */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-60"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-60"
          animate={{
            x: [0, -50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />

        {/* Floating Icons - Light Gray */}
        <motion.div
          className="absolute top-1/4 left-1/4 text-gray-200/40 hidden lg:block"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineCamera size={80} />
        </motion.div>

        <motion.div
          className="absolute bottom-1/4 right-1/4 text-gray-200/40 hidden lg:block"
          animate={{
            y: [0, 20, 0],
            rotate: [0, -10, 10, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineCollection size={90} />
        </motion.div>
      </div>

      {/* Hero Section - Light Gray */}
      <div className="relative w-full h-[50vh] sm:h-[55vh] md:h-[60vh] lg:h-[65vh] overflow-hidden">
        <motion.img
          variants={heroVariants}
          initial="hidden"
          animate="visible"
          src="assets/Committee.png"
          alt="Gallery Background"
          className="w-full h-full object-cover object-center"
        />

        {/* Light Gradient Overlay - Gray */}
        <div className="absolute inset-0 bg-gradient-to-r from-gray-200/80 via-gray-100/70 to-gray-200/80" />

        {/* Floating Elements - Light Gray */}
        <motion.div
          className="absolute top-20 left-10 text-gray-300/40 hidden lg:block"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineSparkles size={70} />
        </motion.div>

        <motion.div
          className="absolute bottom-20 right-10 text-gray-300/40 hidden lg:block"
          animate={{
            y: [0, 20, 0],
            rotate: [0, -10, 10, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineHeart size={60} />
        </motion.div>

        {/* Hero Content - Dark text for contrast */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 sm:px-6">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4 text-gray-800 drop-shadow-md"
          >
            Alumni Gallery
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-sm sm:text-base md:text-lg lg:text-xl font-medium text-gray-700 max-w-2xl px-4 leading-relaxed"
          >
            Relive cherished memories and stay connected with our vibrant alumni
            community through photos and videos.
          </motion.p>

          {/* Stats Counter - Light Gray */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex items-center space-x-4 mt-6 sm:mt-8 bg-white/40 backdrop-blur-md rounded-full px-4 sm:px-6 py-2 sm:py-3 border border-gray-300/60"
          >
            <div className="flex items-center space-x-2">
              <HiOutlinePhotograph className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              <span className="text-gray-600 text-xs sm:text-sm font-medium">
                {galleryData.images.length} Photos
              </span>
            </div>
            <div className="w-px h-4 bg-gray-300" />
            <div className="flex items-center space-x-2">
              <HiOutlineVideoCamera className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              <span className="text-gray-600 text-xs sm:text-sm font-medium">
                {galleryData.videos.length} Videos
              </span>
            </div>
          </motion.div>

          {/* Scroll Indicator - Light Gray */}
          <motion.div
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center backdrop-blur-sm">
              <div className="w-1 h-2 bg-gray-400 rounded-full mt-2" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Tabs - Light Gray */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="flex justify-center gap-4 py-10 px-4 md:px-10"
      >
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`px-6 sm:px-8 py-3 sm:py-4 rounded-full transition-all duration-300 text-base sm:text-lg font-bold shadow-lg hover:shadow-xl flex items-center space-x-2 ${
            activeTab === "post"
              ? "bg-gray-700 text-white shadow-lg"
              : "bg-white text-gray-600 border-2 border-gray-200 hover:border-gray-400"
          }`}
          onClick={() => setActiveTab("post")}
        >
          <HiOutlinePhotograph className="w-5 h-5" />
          <span>Photos</span>
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`px-6 sm:px-8 py-3 sm:py-4 rounded-full transition-all duration-300 text-base sm:text-lg font-bold shadow-lg hover:shadow-xl flex items-center space-x-2 ${
            activeTab === "video"
              ? "bg-gray-700 text-white shadow-lg"
              : "bg-white text-gray-600 border-2 border-gray-200 hover:border-gray-400"
          }`}
          onClick={() => setActiveTab("video")}
        >
          <HiOutlineVideoCamera className="w-5 h-5" />
          <span>Videos</span>
        </motion.button>
      </motion.div>

      {/* Content */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="p-4 md:p-8 bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl mx-4 md:mx-8 mb-8 border border-gray-200"
      >
        {loading ? (
          <SkeletonLoader />
        ) : error ? (
          <motion.div
            variants={itemVariants}
            className="text-center p-8 bg-red-50 border-2 border-red-200 rounded-2xl max-w-md mx-auto"
          >
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="text-red-600 text-lg font-semibold">{error}</p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {activeTab === "post" ? (
              galleryData.images.length > 0 ? (
                galleryData.images.map((img, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    whileHover={{ y: -8, scale: 1.02 }}
                    className="group cursor-pointer"
                    onClick={() => handleMediaClick(img, index)}
                  >
                    <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-200">
                      {/* Image Container - Reduced height */}
                      <div className="relative h-48 overflow-hidden bg-gray-100">
                        <motion.img
                          src={img.url}
                          alt={`Gallery ${index + 1}`}
                          className="w-full h-full object-cover"
                          whileHover={{ scale: 1.1 }}
                          transition={{ duration: 0.5 }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://via.placeholder.com/400x300?text=Image+Not+Found";
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                        {/* Overlay Icons */}
                        <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <span className="text-white text-sm font-medium bg-black/50 px-3 py-1 rounded-full backdrop-blur-sm">
                            Photo {index + 1}
                          </span>
                          <div className="flex space-x-2">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
                            >
                              <HiOutlineHeart className="w-4 h-4 text-white" />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
                            >
                              <HiOutlineDownload className="w-4 h-4 text-white" />
                            </motion.button>
                          </div>
                        </div>
                      </div>

                      {/* College Name - Below the image */}
                      <div className="p-3 border-t border-gray-100">
                        <div className="flex items-center space-x-2">
                          <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                            <HiOutlineOfficeBuilding className="w-3 h-3 text-gray-600" />
                          </div>
                          <span className="text-xs font-medium text-gray-700 truncate">
                            {img.collegeName}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              ) : (
                <motion.div
                  variants={itemVariants}
                  className="text-center w-full text-gray-500 col-span-full py-16"
                >
                  <div className="w-20 h-20 mx-auto bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4 border border-gray-200">
                    <HiOutlinePhotograph className="w-10 h-10 text-gray-500" />
                  </div>
                  <p className="text-lg text-gray-600">No photos to show yet</p>
                  <p className="text-sm text-gray-400 mt-2">Check back soon for memories!</p>
                </motion.div>
              )
            ) : galleryData.videos.length > 0 ? (
              galleryData.videos.map((video, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className="group cursor-pointer"
                  onClick={() => handleMediaClick(video, index)}
                >
                  <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-200">
                    {/* Video Container - Reduced height */}
                    <div className="relative h-48 overflow-hidden bg-gray-100">
                      <video
                        src={video.url}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gray-900/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <motion.div
                          whileHover={{ scale: 1.2 }}
                          className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"
                        >
                          <svg
                            className="w-6 h-6 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                            />
                          </svg>
                        </motion.div>
                      </div>

                      {/* Video Duration Badge (placeholder) */}
                      <div className="absolute bottom-4 right-4 bg-black/50 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full border border-white/20">
                        2:30
                      </div>
                    </div>

                    {/* College Name - Below the video */}
                    <div className="p-3 border-t border-gray-100">
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                          <HiOutlineOfficeBuilding className="w-3 h-3 text-gray-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-700 truncate">
                          {video.collegeName}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <motion.div
                variants={itemVariants}
                className="text-center w-full text-gray-500 col-span-full py-16"
              >
                <div className="w-20 h-20 mx-auto bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4 border border-gray-200">
                  <HiOutlineVideoCamera className="w-10 h-10 text-gray-500" />
                </div>
                <p className="text-lg text-gray-600">No videos to show yet</p>
                <p className="text-sm text-gray-400 mt-2">Video content coming soon!</p>
              </motion.div>
            )}
          </div>
        )}
      </motion.div>

      {/* Lightbox Modal - Dark but with gray accents */}
      <AnimatePresence>
        {selectedMedia && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-gray-900/95 flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={() => setSelectedMedia(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-6xl w-full max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setSelectedMedia(null)}
                className="absolute -top-12 right-0 text-gray-300 hover:text-white z-50 p-2"
              >
                <HiOutlineX className="w-8 h-8" />
              </motion.button>

              {/* Navigation Buttons */}
              <div className="absolute top-1/2 left-4 transform -translate-y-1/2 z-50">
                <motion.button
                  whileHover={{ scale: 1.1, x: -2 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handlePrev}
                  className="p-3 bg-gray-800/50 backdrop-blur-md rounded-full hover:bg-gray-700/70 transition-colors border border-gray-600"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </motion.button>
              </div>

              <div className="absolute top-1/2 right-4 transform -translate-y-1/2 z-50">
                <motion.button
                  whileHover={{ scale: 1.1, x: 2 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleNext}
                  className="p-3 bg-gray-800/50 backdrop-blur-md rounded-full hover:bg-gray-700/70 transition-colors border border-gray-600"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </motion.button>
              </div>

              {/* Media Display */}
              <div className="relative rounded-2xl overflow-hidden bg-gray-800/50 backdrop-blur-sm border border-gray-700">
                {activeTab === 'post' ? (
                  <motion.img
                    src={selectedMedia.url}
                    alt="Full size"
                    className="w-full h-full object-contain max-h-[85vh]"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  />
                ) : (
                  <motion.video
                    src={selectedMedia.url}
                    controls
                    autoPlay
                    className="w-full h-full object-contain max-h-[85vh]"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  />
                )}

                {/* Media Info Bar */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-gray-900/90 to-transparent p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2"
                >
                  <div className="flex items-center space-x-2">
                    <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-300" />
                    <span className="text-gray-300 text-sm">
                      {selectedMedia?.collegeName}
                    </span>
                  </div>
                  <span className="text-gray-300 text-sm">
                    {activeTab === 'post' ? 'Photo' : 'Video'} {selectedMediaIndex + 1} of {activeTab === 'post' ? galleryData.images.length : galleryData.videos.length}
                  </span>
                  <div className="flex space-x-3">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="p-2 bg-gray-700/50 backdrop-blur-sm rounded-full hover:bg-gray-600/70 transition-colors border border-gray-600"
                    >
                      <HiOutlineHeart className="w-5 h-5 text-gray-200" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="p-2 bg-gray-700/50 backdrop-blur-sm rounded-full hover:bg-gray-600/70 transition-colors border border-gray-600"
                    >
                      <HiOutlineShare className="w-5 h-5 text-gray-200" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="p-2 bg-gray-700/50 backdrop-blur-sm rounded-full hover:bg-gray-600/70 transition-colors border border-gray-600"
                    >
                      <HiOutlineDownload className="w-5 h-5 text-gray-200" />
                    </motion.button>
                  </div>
                </motion.div>
              </div>

              {/* Counter Dots */}
              <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {[...Array(activeTab === 'post' ? galleryData.images.length : galleryData.videos.length)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0 }}
                    animate={{ scale: i === selectedMediaIndex ? 1.2 : 1 }}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      i === selectedMediaIndex ? 'bg-gray-300' : 'bg-gray-500/50'
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Gallery;